import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Fragt-cutoff: viser hvornår vi sender næste gang.
 *
 * Data hentes fra LOCKTOWNs fragt-API, fx:
 * { today: false, tomorrow: true, remaining: 90270, clock: 16,
 *   cutoffAt: '2026-09-21T14:00:00+00:00', nowAt: '2026-09-20T12:55:29+00:00' }
 *
 * Teksterne kommer fra block settings som data-attributter, så de kan
 * oversættes i Translate & Adapt. Pladsholderne er [kl] og [dag].
 * [tid] er et alias for [kl], så ældre tekster med nedtælling stadig virker.
 *
 * [kl] bliver til "kl. 16": prefikset er en egen indstilling og står inde i
 * fremhævningen sammen med tallet, så de farves som én enhed.
 *
 * Er den valgte udgave udsolgt, sætter Liquid data-sold-out, og så bliver
 * blocken skjult: der er ingen grund til at love afsendelse i dag på en vare,
 * der ikke kan lægges i kurven. I editoren vises den alligevel, så den kan
 * redigeres. Ved variantskift læses den nye tilstand fra det server-rendrede
 * svar, så et skift fra en udsolgt til en købbar størrelse viser den igen.
 */

/** Butikkens tidszone. Bruges til ugedag og midnats-opdatering. */
const TIMEZONE = 'Europe/Copenhagen';

/** Hvor længe et svar genbruges på tværs af blocks på siden (ms). */
const CACHE_TTL = 60_000;

/** setTimeout kan ikke håndtere større værdier. */
const MAX_TIMEOUT = 2_147_483_647;

/** @typedef {{ today: boolean, tomorrow: boolean, remaining: number, clock: number, cutoffAt?: string, receivedAt: number }} CutoffData */

/** @type {Map<string, { expires: number, request: Promise<CutoffData> }>} */
const cache = new Map();

/**
 * Henter cutoff-data. Flere blocks på samme side deler ét kald.
 * @param {string} endpoint
 * @param {boolean} [force] - Spring cachen over.
 * @returns {Promise<CutoffData>}
 */
function loadCutoff(endpoint, force = false) {
  const cached = cache.get(endpoint);
  if (!force && cached && cached.expires > Date.now()) return cached.request;

  const request = fetch(endpoint, { headers: { Accept: 'application/json' } })
    .then((response) => {
      if (!response.ok) throw new Error(`[locktown-shipping-cutoff] HTTP ${response.status}`);
      return response.json();
    })
    .then((data) => ({ ...data, receivedAt: Date.now() }));

  request.catch(() => cache.delete(endpoint));
  cache.set(endpoint, { expires: Date.now() + CACHE_TTL, request });

  return request;
}

/**
 * Sekunder tilbage til cutoff, talt videre lokalt siden svaret kom.
 * @param {CutoffData} data
 */
function secondsLeft(data) {
  return Math.round(data.remaining - (Date.now() - data.receivedAt) / 1000);
}

/**
 * Cutoff-tidspunktet i butikkens tidszone, fx "16" eller "16.30".
 * @param {CutoffData} data
 * @param {string} locale
 */
function formatClock(data, locale) {
  const date = data.cutoffAt ? new Date(data.cutoffAt) : null;

  if (date && !Number.isNaN(date.getTime())) {
    const parts = new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZone: TIMEZONE,
    }).formatToParts(date);

    const hour = parts.find((part) => part.type === 'hour')?.value;
    const minute = parts.find((part) => part.type === 'minute')?.value;

    if (hour != null && minute != null) {
      return minute === '00' ? String(Number(hour)) : `${Number(hour)}.${minute}`;
    }
  }

  return String(data.clock ?? '');
}

/**
 * Ugedagen for cutoff i butikkens tidszone, fx "mandag".
 * @param {CutoffData} data
 * @param {string} locale
 */
function formatWeekday(data, locale) {
  const date = data.cutoffAt ? new Date(data.cutoffAt) : null;
  if (!date || Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: TIMEZONE }).format(date);
}

/** Millisekunder til næste midnat i butikkens tidszone. */
function msUntilMidnight() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    timeZone: TIMEZONE,
  }).formatToParts(new Date());

  const value = (type) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  const elapsed = value('hour') * 3600 + value('minute') * 60 + value('second');

  return (86_400 - elapsed) * 1000;
}

class LocktownShippingCutoff extends Component {
  requiredRefs = ['text'];

  /** @type {CutoffData | null} */
  #data = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #refreshTimer = null;
  #refreshing = false;
  /** Den skabelon der p.t. står i DOM'en. */
  /** @type {string | null} */
  #shape = null;
  /** Tekstknuderne for [tid], [kl] og [dag]. */
  /** @type {Map<string, Text>} */
  #slots = new Map();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#onVariantSelect);
    this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#scope?.removeEventListener(StandardEvents.productSelect, this.#onVariantSelect);
    this.#stopTimers();
  }

  /** Sektionen, eller dialogen ved quick-add. Variantskift sendes indenfor den. */
  get #scope() {
    return this.closest('.shopify-section, dialog');
  }

  /** Udsolgt og ikke i editoren: så skal blocken ikke vises. */
  get #hiddenBySoldOut() {
    return this.dataset.soldOut === 'true' && this.dataset.designMode !== 'true';
  }

  /** Kaldes når morph har skiftet indholdet ud, fx ved variant-skift. */
  updatedCallback() {
    super.updatedCallback();
    // morph har skiftet indholdet ud, så de gemte tekstknuder er væk.
    this.#shape = null;
    this.#slots.clear();
    if (this.#data) this.#render();
  }

  /**
   * @param {boolean} [force]
   */
  #load(force = false) {
    const endpoint = this.dataset.endpoint;
    if (!endpoint) return;

    this.#refreshing = true;

    loadCutoff(endpoint, force)
      .then((data) => {
        this.#data = data;
        this.#render();
        this.#startTimers();
      })
      .catch((error) => {
        console.warn('[locktown-shipping-cutoff] Kunne ikke hente fragt-cutoff:', error);
        if (!this.#data) this.#renderDesignModeExample();
      })
      .finally(() => {
        this.#refreshing = false;
      });
  }

  #render() {
    if (this.#hiddenBySoldOut) {
      this.hidden = true;
      return;
    }

    const data = this.#data;
    if (!data) return;

    const locale = this.dataset.locale || 'da';
    const clock = formatClock(data, locale);

    if (data.today) {
      // Cutoff er passeret: hent ny besked i stedet for at vise et tidspunkt, der er gået.
      if (secondsLeft(data) <= 0) {
        this.#refresh();
        return;
      }
      this.#write(this.dataset.textToday, { kl: clock, tid: clock });
    } else if (data.tomorrow) {
      this.#write(this.dataset.textTomorrow, { kl: clock });
    } else {
      this.#write(this.dataset.textLater, {
        dag: formatWeekday(data, locale),
        kl: clock,
      });
    }
  }

  /** I editoren vises et eksempel, så blocken ikke er usynlig, hvis API'et svigter. */
  #renderDesignModeExample() {
    if (this.dataset.designMode !== 'true') return;

    this.#write(this.dataset.textTomorrow, { kl: '16' });
  }

  /**
   * Skriver teksten og erstatter [tid], [kl] og [dag].
   *
   * Strukturen bygges kun om, når skabelonen skifter. Ellers opdateres
   * tekstknuderne, så DOM'en bliver stående.
   *
   * @param {string | undefined} template
   * @param {Record<string, string>} values
   */
  #write(template, values) {
    if (!template) return;

    if (template !== this.#shape) {
      this.#build(template, values);
      this.#shape = template;
    } else {
      for (const [key, value] of Object.entries(values)) {
        const node = this.#slots.get(key);
        if (node && node.nodeValue !== value) node.nodeValue = value;
      }
    }

    this.hidden = false;
  }

  /**
   * @param {string} template
   * @param {Record<string, string>} values
   */
  #build(template, values) {
    const { text } = this.refs;
    const fragment = document.createDocumentFragment();

    this.#slots.clear();

    for (const part of template.split(/(\[tid\]|\[kl\]|\[dag\])/g)) {
      const key = part.startsWith('[') && part.endsWith(']') ? part.slice(1, -1) : null;

      if (key) {
        const value = values[key];
        if (value == null) continue;

        const node = document.createTextNode(value);
        this.#slots.set(key, node);

        if (key === 'tid' || key === 'kl') {
          const span = document.createElement('span');
          span.className = 'locktown-shipping-cutoff__time';

          const prefix = this.dataset.timePrefix?.trim();
          if (prefix) span.append(`${prefix} `);

          span.append(node);
          fragment.append(span);
        } else {
          fragment.append(node);
        }
      } else if (part) {
        fragment.append(document.createTextNode(part));
      }
    }

    text.replaceChildren(fragment);
  }

  #startTimers() {
    this.#stopTimers();

    // Opdater igen, når cutoff passerer, eller når datoen skifter.
    const data = this.#data;
    const untilCutoff = data ? secondsLeft(data) * 1000 : Infinity;
    const delay = Math.min(untilCutoff > 0 ? untilCutoff : Infinity, msUntilMidnight()) + 2000;

    this.#refreshTimer = setTimeout(() => this.#refresh(), Math.min(Math.max(delay, 5000), MAX_TIMEOUT));
  }

  #stopTimers() {
    if (this.#refreshTimer) clearTimeout(this.#refreshTimer);
    this.#refreshTimer = null;
  }

  #refresh() {
    if (this.#refreshing) return;
    this.#stopTimers();
    this.#load(true);
  }

  #handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;
    if (this.#data && Date.now() - this.#data.receivedAt < CACHE_TTL) return;

    this.#refresh();
  };

  #onVariantSelect = (event) => {
    // Swatches på produktkort i samme sektion handler om et andet produkt.
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;

        if (detail.newProduct) {
          this.dataset.productId = detail.newProduct.id;
        } else if (detail.productId && String(detail.productId) !== this.dataset.productId) {
          return;
        }

        // Find blockens egen tvilling i den nyrendrede HTML via data-block-id.
        const fresh = detail.html.querySelector(
          `locktown-shipping-cutoff-component[data-block-id="${this.dataset.blockId}"]`
        );
        if (!(fresh instanceof HTMLElement)) return;

        if (fresh.dataset.soldOut === 'true') {
          this.dataset.soldOut = 'true';
        } else {
          delete this.dataset.soldOut;
        }

        if (this.#hiddenBySoldOut) {
          this.hidden = true;
        } else if (this.#data) {
          this.#render();
        }
      })
      .catch((error) => {
        if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') return;
        console.warn(error);
      });
  };
}

if (!customElements.get('locktown-shipping-cutoff-component')) {
  customElements.define('locktown-shipping-cutoff-component', LocktownShippingCutoff);
}
